// src/pages/PromoCode.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Card,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  DatePicker
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  DollarOutlined,
  CalendarOutlined,
  TagsOutlined
} from "@ant-design/icons";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import BASEURL from '../API/Config';

const { Title, Text } = Typography;

const PromoCode = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState("");

  const token = localStorage.getItem('token');

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASEURL}/master/getpromocode`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch promo codes.');
      const data = await response.json();
      setPromoCodes(data.data || []);
    } catch (error) {
      console.error(error);
      message.error('Failed to load promo codes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, [token]);

  const filteredPromoCodes = promoCodes.filter((promo) =>
    promo.promoCodeName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (values) => {
    const method = editing ? 'PUT' : 'POST';
    const endpoint = editing
      ? `${BASEURL}/master/updatepromocode/${editingId}`
      : `${BASEURL}/master/createpromode`;

    // Robust date formatting for API
    const payload = {
      ...values,
      validate: values.validate ? values.validate.format('YYYY-MM-DD') : "",
      id: editingId
    };

    try {
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to save promo code.');

      message.success(editing ? 'Promo Code Updated' : 'Promo Code Created');
      setShowModal(false);
      form.resetFields();
      fetchPromoCodes();
    } catch (error) {
      console.error(error);
      message.error('Failed to save promo code.');
    }
  };

  const handleEdit = (record) => {
    setEditingId(record._id);
    setEditing(true);
    form.setFieldsValue({
      promoCodeName: record.promoCodeName,
      validate: record.validate ? dayjs(record.validate) : null,
      offer: record.offer ? record.offer.toString().replace('%', '') : "",
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${BASEURL}/master/deletepromocode/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete promo code.');
      message.success('Promo code deleted successfully');
      fetchPromoCodes();
    } catch (error) {
      console.error(error);
      message.error('Failed to delete promo code.');
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'index',
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: 'Promo Code',
      dataIndex: 'promoCodeName',
      key: 'name',
      render: (text) => <Text strong style={{ color: 'var(--primary-color)' }}>{text}</Text>,
    },
    {
      title: 'Valid Until',
      dataIndex: 'validate',
      key: 'validate',
      render: (date) => {
        const d = dayjs(date);
        const daysLeft = d.diff(dayjs(), 'day');
        return (
          <Space direction="vertical" size={0}>
            <Text>{d.format('DD/MM/YYYY')}</Text>
            {daysLeft >= 0 ? (
              <Text type="secondary" style={{ fontSize: '12px' }}>{daysLeft} days remaining</Text>
            ) : (
              <Text type="danger" style={{ fontSize: '12px' }}>Expired</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Offer',
      dataIndex: 'offer',
      key: 'offer',
      render: (text) => <Tag color="blue" style={{ borderRadius: '6px' }}>{text} OFF</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (record) => {
        const isValid = record.validate && dayjs(record.validate).isAfter(dayjs());
        return <Tag color={isValid ? 'success' : 'error'}>{isValid ? 'ACTIVE' : 'EXPIRED'}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Promo Code?"
              description="Are you sure you want to delete this promo code?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                shape="circle"
                icon={<DeleteOutlined />}
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Flipkart-style page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Marketing</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Promo Codes</h2>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setEditing(false); setEditingId(""); form.resetFields(); setShowModal(true); }}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}
        >
          New Promo Code
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Input
            allowClear
            placeholder="Search by promo code name..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 280, borderRadius: 4 }}
          />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filteredPromoCodes.length}</strong>
          </div>
        </div>
        <div>
          <Table
            columns={columns}
            dataSource={filteredPromoCodes}
            rowKey="_id"
            loading={loading}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"], showTotal: (t) => `${t} promo codes` }}
            size="middle"
          />
        </div>
      </div>

      <Modal
        title={<Title level={4} style={{ margin: 0 }}>{editing ? "Update Promo Code" : "Create New Promo Code"}</Title>}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="promoCodeName"
            label="Promo Code Name"
            rules={[{ required: true, message: 'Please enter promo code name' }]}
          >
            <Input prefix={<TagsOutlined />} placeholder="e.g., WELCOME20" size="large" />
          </Form.Item>

          <Form.Item
            name="validate"
            label="Expiry Date"
            rules={[{ required: true, message: 'Please select expiry date' }]}
          >
            <DatePicker
              suffixIcon={<CalendarOutlined />}
              style={{ width: '100%', height: '45px' }}
              placeholder="YYYY-MM-DD"
              format="YYYY-MM-DD"
              inputReadOnly={true}
              popupClassName="senior-datepicker-popup"
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item
            name="offer"
            label="Discount Percentage (%)"
            rules={[
              { required: true, message: 'Please enter offer' },
              { pattern: /^[0-9]+$/, message: 'Please enter a valid number' }
            ]}
          >
            <Input prefix={<DollarOutlined />} placeholder="Percentage discount (e.g., 15)" size="large" />
          </Form.Item>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {editing ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PromoCode;


